import React, { useEffect, useState } from "react";
import GlobalVariable from "../../GlobalVariable";
import api from "../../api";
import './Comments.css';

type Staff = {
    id: number;
    name?: string | null;
    phone?: string | null;
};

type CommentType = {
    id: number;
    author: Staff | null;
    text: string;
    likes: number;
    parentId?: number | null;
    createdAt: string;
    deletedAt?: string | null;
    canDelete?: boolean;
};

function timeAgo(date: string) {
    const d = new Date(date);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "только что";
    if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;

    return d.toLocaleString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function getErrorMessage(error: any, fallback: string) {
    return error?.response?.data?.error || fallback;
}

export default function Comments({ lessonId }: { lessonId: number }) {
    const [comments, setComments] = useState<CommentType[]>([]);
    const [newComment, setNewComment] = useState("");
    const [replyText, setReplyText] = useState("");
    const [replyTo, setReplyTo] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadComments = React.useCallback(() => {
        setLoading(true);
        setError("");

        api.get(`${GlobalVariable.staffBack()}/api/lesson/${lessonId}/comments`, {
            withCredentials: true,
        })
            .then(res => setComments(res.data))
            .catch((requestError) => setError(getErrorMessage(requestError, "Ошибка загрузки комментариев")))
            .finally(() => setLoading(false));
    }, [lessonId]);

    useEffect(() => {
        setReplyTo(null);
        setReplyText("");
        setNewComment("");
        loadComments();
    }, [loadComments, lessonId]);

    const renderComments = (parentId: number | null = null, level = 0) =>
        comments
            .filter(comment => (comment.parentId ?? null) === parentId)
            .map(comment => {
                const isDeleted = !!comment.deletedAt;
                const authorLabel = comment.author?.name?.trim()
                    || comment.author?.phone?.trim()
                    || (isDeleted ? "—" : "Аноним");

                return (
                    <div
                        key={comment.id}
                        className={`comment${level ? ' comment-reply' : ''}${isDeleted ? ' comment-deleted' : ''}`}
                    >
                        <div className="comment-header">
                            <span className="comment-author">{authorLabel}</span>
                            <span className="comment-date">{timeAgo(comment.createdAt)}</span>
                        </div>
                        <div className="comment-text">
                            {isDeleted
                                ? (
                                    <span style={{ fontStyle: "italic", color: "#d83a00" }}>
                                        Комментарий удалён {comment.deletedAt ? timeAgo(comment.deletedAt) : ""}
                                    </span>
                                )
                                : comment.text
                            }
                        </div>
                        {!isDeleted && (
                            <div className="comment-actions">
                                <button className="comment-action" type="button" onClick={() => likeComment(comment.id)}>
                                    <span aria-hidden="true">👍</span> {comment.likes > 0 ? comment.likes : ""}
                                </button>
                                <button className="comment-action" type="button" onClick={() => {
                                    setReplyTo(comment.id);
                                    setReplyText("");
                                    setError("");
                                }}>
                                    Ответить
                                </button>
                                {comment.canDelete && (
                                    <button
                                        className="comment-action"
                                        type="button"
                                        onClick={() => handleDelete(comment.id)}
                                        style={{ color: "#e72f44", marginLeft: 8 }}
                                    >
                                        Удалить
                                    </button>
                                )}
                            </div>
                        )}
                        {replyTo === comment.id && !isDeleted && (
                            <form className="comment-form reply-form" onSubmit={e => submitComment(e, comment.id)}>
                                <textarea
                                    className="comment-input"
                                    placeholder="Ваш ответ..."
                                    value={replyText}
                                    onChange={e => setReplyText(e.target.value)}
                                    required
                                />
                                <button className="comment-btn" type="submit">Отправить</button>
                                <button className="comment-btn-cancel" type="button" onClick={() => {
                                    setReplyTo(null);
                                    setReplyText("");
                                }}>
                                    Отмена
                                </button>
                            </form>
                        )}
                        {renderComments(comment.id, level + 1)}
                    </div>
                )
            });

    function likeComment(id: number) {
        setError("");

        api.post(
            `${GlobalVariable.staffBack()}/api/comment/${id}/like`,
            {},
            { withCredentials: true }
        ).then(res => {
            setComments(prev =>
                prev.map(comment => comment.id === id ? { ...comment, likes: res.data.likes } : comment)
            );
        }).catch((requestError) => setError(getErrorMessage(requestError, "Ошибка лайка")));
    }

    function submitComment(e: React.FormEvent, parentId: number | null = null) {
        e.preventDefault();

        const text = parentId ? replyText : newComment;
        if (!text.trim()) return;

        setError("");

        api.post(
            `${GlobalVariable.staffBack()}/api/lesson/${lessonId}/comment`,
            {
                text,
                parentId: parentId ?? null
            },
            { withCredentials: true }
        )
            .then(() => {
                loadComments();
                if (parentId) {
                    setReplyText("");
                    setReplyTo(null);
                } else {
                    setNewComment("");
                }
            })
            .catch((requestError) => setError(getErrorMessage(requestError, "Ошибка отправки комментария")));
    }

    function handleDelete(commentId: number) {
        if (!window.confirm("Удалить комментарий?")) return;

        setError("");

        api.delete(
            `${GlobalVariable.staffBack()}/api/comment/${commentId}`,
            { withCredentials: true }
        )
            .then(() => loadComments())
            .catch((requestError) => setError(getErrorMessage(requestError, "Ошибка удаления")));
    }

    return (
        <div className="comments-section">
            <h5 className="comments-title">Комментарии:</h5>
            {error && <div className="error">{error}</div>}
            <form className="comment-form" onSubmit={e => submitComment(e)}>
                <textarea
                    className="comment-input"
                    placeholder="Добавьте комментарий..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    required
                />
                <button className="comment-btn" type="submit">Отправить</button>
            </form>
            {loading ? (
                <div className="loading">Загрузка...</div>
            ) : (
                <div className="comments-list">
                    {renderComments()}
                </div>
            )}
        </div>
    );
}
